import { getRandomInt } from './number';

/**
 * Generates a portion of a random UID for DICOM use. The leading digit will never be a 0 if n has been
 * set to something other than 1.
 * @param n Number of digits to generate into UID.
 * @param isRecursive Set to true when this function loops recursively. Don't set this parameter manually.
 */
export function generateRandomUID(n: number, isRecursive = false): string {
    // generate random UID (0-9) of a fixed length
    const add = 1;
    let max = 12 - add; // 12 is the min safe number Math.random() can generate without it starting to pad the end with zeros.
    let uid: string;

    if (n > max) {
        uid = generateRandomUID(max, true) + generateRandomUID(n - max, true);
    } else {
        max = Math.pow(10, n + add);
        const min = max / 10;
        const number = Math.floor(Math.random() * (max - min + 1)) + min;
        uid = number.toString().substring(add);
    }

    if (!isRecursive && n !== 1) {
        // if we're at the root of the recursion, don't start UID with a leading zero (unless n == 1)
        return uid.charAt(0) !== '0'
            ? uid
            : getRandomInt(1, 9).toString() + uid.substring(1);
    } else {
        return uid;
    }
}

/** Generates a random UID for DICOM use. */
export function generateUID(): string {
    return '2.25.' + generateRandomUID(39);
}

/**
 * Type guard for loosely UID-like strings.
 *
 * Determines whether the provided value is a string that matches the pattern of a DICOM UID.
 * A DICOM UID is a string consisting of numbers separated by dots (e.g., "1.2.840.10008.1.2").
 *
 * @param uid - The value to check for UID-like format.
 * @returns `true` if the value is a string and matches the DICOM UID pattern; otherwise, `false`.
 */
export function isUidLike(uid: unknown): uid is string {
    return typeof uid === 'string' && /^\d+(\.\d+)+$/.test(uid);
}
